import { useQuery } from '@apollo/client';
import gql from 'graphql-tag';
import { DateTime } from 'luxon';

import { useI18n, useSelector } from '~Hooks';
import { GetOffers, GetOffersVariables, GetOffers_Company_allPromoCodes } from './__queries__';
import { SERVICE_TYPE } from '../../__queries__';

interface FindByPromoCodeResult {
  success: boolean;
  promoCode?: GetOffers_Company_allPromoCodes;
  errorMessage?: string;
}

const GET_OFFERS = gql`
  query GetOffers($companyId: ID!) {
    Company(id: $companyId) {
      id

      allPromoCodes {
        id
        code
        active
        uses
        createdAt
        venues
        categoryIds
        discountType
        discountRate
        offerType
        uses
        title {
          en
          se
          es
        }
        useLimit
        requiresUser
        activatesAt
        expiresAt
        activeBetweenHours
        serviceTypes
      }

      offers(context: [POS]) {
        id
        name
        type
        enabled
        deleted
        discountRate
        discountType
        overrideItemPrices

        nameLang {
          id
          se
          en
          es
          no
        }

        items(filter: { deleted: false }) {
          id
          titleLang {
            id
            se
            en
            es
            no
          }
        }

        categories {
          id
          items {
            id
          }
        }

        stages {
          id
          title {
            id
            se
            en
            es
            no
          }
          items(filter: { deleted: false }) {
            id
          }
        }
      }
    }
  }
`;

export default function useOffers() {
  const companyId = useSelector((state) => state.company);
  const venueId = useSelector((state) => state.venueId) as string;
  const { i18n } = useI18n();

  const { data, loading } = useQuery<GetOffers, GetOffersVariables>(GET_OFFERS, {
    variables: { companyId },
    skip: !companyId,
  });

  const filteredOffers =
    data?.Company?.offers.filter((offer) => !offer.deleted && offer.enabled) || [];

  /** Lookup table: offer id or promo code id -> resolved title, for both offers and promo codes */
  const titleById: Record<string, string> = {};
  data?.Company?.offers.forEach((offer) => {
    titleById[offer.id] = i18n(offer.nameLang);
  });
  data?.Company?.allPromoCodes.forEach((promoCode) => {
    titleById[promoCode.id] = i18n(promoCode.title);
  });

  /** Lookup table: promo code id -> the full promo code object */
  const promoCodeById: Record<string, GetOffers_Company_allPromoCodes> = {};
  data?.Company?.allPromoCodes.forEach((promoCode) => {
    promoCodeById[promoCode.id] = promoCode;
  });

  // Checks activation window, active flag and venue eligibility (excludes the code match itself)
  const isPromoCodeValid = (promoCode: GetOffers_Company_allPromoCodes) => {
    const now = DateTime.utc();

    if (now < DateTime.fromISO(promoCode.activatesAt, { zone: 'utc' })) {
      return false;
    }

    if (!promoCode.active) {
      return false;
    }

    // The schema says these lists are non-null, but the API sends null when a code has no restriction.
    if ((promoCode.venues ?? []).length > 0 && !promoCode.venues.includes(venueId)) {
      return false;
    }

    if (now > DateTime.fromISO(promoCode.expiresAt, { zone: 'utc' })) {
      return false;
    }

    return true;
  };

  const findByPromoCode = (code: string, serviceType?: SERVICE_TYPE): FindByPromoCodeResult => {
    const promoCode = data?.Company?.allPromoCodes.find(
      (promo) => promo.code.toLowerCase() === code.toLowerCase(),
    );

    if (!promoCode) {
      return { success: false, errorMessage: i18n('PromoCode.Error.NotFound') };
    }

    const now = DateTime.utc();

    if (now < DateTime.fromISO(promoCode.activatesAt, { zone: 'utc' })) {
      return { success: false, errorMessage: i18n('PromoCode.Error.NotActiveYet') };
    }

    if (!promoCode.active) {
      return { success: false, errorMessage: i18n('PromoCode.Error.NotActive') };
    }

    if ((promoCode.venues ?? []).length > 0 && !promoCode.venues.includes(venueId)) {
      return { success: false, errorMessage: i18n('PromoCode.Error.InvalidVenue') };
    }

    // Null from the API means "any service type" - see isPromoCodeValid.
    if (
      serviceType &&
      (promoCode.serviceTypes ?? []).length > 0 &&
      !promoCode.serviceTypes.includes(serviceType)
    ) {
      return { success: false, errorMessage: i18n('PromoCode.Error.InvalidServiceType') };
    }

    if (now > DateTime.fromISO(promoCode.expiresAt, { zone: 'utc' })) {
      return { success: false, errorMessage: i18n('PromoCode.Error.Expired') };
    }

    return { success: true, promoCode };
  };

  const hasActivePromoCodes = (data?.Company?.allPromoCodes || []).some(isPromoCodeValid);

  return {
    /**
     * All offers for active company
     */
    offers: filteredOffers || [],
    /**
     * Find a promo code by its code
     */
    findByPromoCode,
    /**
     * Boolean. True if any promo code is active, within its activation/expiry window and valid for this venue
     */
    hasActivePromoCodes,
    /**
     * Boolean. Indicates if the query is loading
     */
    loading,
    /**
     * Lookup table: offer id or promo code id -> resolved (localized) title
     */
    titleById,
    /**
     * Lookup table: promo code id -> the full promo code object
     */
    promoCodeById,
  };
}
